import React, { useState } from 'react';
import { PiMapPin } from 'react-icons/pi';
import { Text } from '../../atoms/text';
import { useI18n } from '../../../hooks/provider-bounded/i18n';
import { getConfiguredGoogleMapsApiKey } from '../../../utils/location/google-maps.loader';

interface LocationMapThumbnailProps {
  /** Latitude of the property's selected point. */
  latitude: number;
  /** Longitude of the property's selected point. */
  longitude: number;
}

const buildThumbnailUrl = (
  latitude: number,
  longitude: number
): string | null => {
  const key = getConfiguredGoogleMapsApiKey();
  if (
    !key ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    Math.abs(latitude) > 90 ||
    Math.abs(longitude) > 180
  ) {
    return null;
  }
  const point = `${latitude},${longitude}`;
  const url = new URL('https://maps.googleapis.com/maps/api/staticmap');
  url.searchParams.set('center', point);
  url.searchParams.set('zoom', '16');
  url.searchParams.set('size', '112x80');
  url.searchParams.set('scale', '2');
  url.searchParams.set('maptype', 'roadmap');
  url.searchParams.set('markers', `size:small|color:red|${point}`);
  url.searchParams.set('key', key);
  return url.toString();
};

/** Non-interactive map image that fits inside a location action without nested controls. */
export const LocationMapThumbnail: React.FC<LocationMapThumbnailProps> = ({
  latitude,
  longitude,
}) => {
  const { t } = useI18n();
  const url = buildThumbnailUrl(latitude, longitude);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const unavailable = !url || failedUrl === url;

  return (
    <span className="relative flex h-20 w-28 shrink-0 flex-col items-center justify-center gap-1 overflow-hidden rounded-lg bg-white text-center">
      <PiMapPin aria-hidden="true" className="h-5 w-5 text-neutral-500" />
      {unavailable ? (
        <Text as="span" size="tiny" className="px-1 text-neutral-600">
          {t('shared.location.map.thumbnailUnavailable')}
        </Text>
      ) : (
        <img
          key={url}
          src={url}
          alt={t('shared.location.map.thumbnailAlt')}
          width={112}
          height={80}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-contain"
          onError={() => setFailedUrl(url)}
        />
      )}
    </span>
  );
};
